import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, ToggleButton } from '@mui/material';
import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInput, ToggleButtonGroupComponent, useSelectedOrganisation } from '../../../shared';
import { useSaveComment } from '../../hooks';
import { IComment, ICommentForm } from '../../models';
import { useCommentSchema } from '../../validators';
import { commentFromFormMapper, commentToFormMapper } from '../../mappers/comment.mapper';

interface Props {
    supportCase: string;
    comment?: IComment;
    handleClose?: () => void;
}

export const SaveCommentComponent: FC<Props> = ({ supportCase, comment, handleClose }) => {
    const { t } = useTranslation();
    const { organisation } = useSelectedOrganisation();

    const form = useForm<ICommentForm>({
        resolver: yupResolver(useCommentSchema()),
    });
    const watchMessage = form.watch('message');

    useEffect(() => {
        form.reset(commentToFormMapper(comment));
    }, [form, comment]);

    const { mutateAsync: saveComment } = useSaveComment();

    const onSubmit = async (data: ICommentForm) => {
        if (organisation) {
            await saveComment({
                supportCase,
                comment: commentFromFormMapper(data, organisation.id, comment),
            });

            if (!comment) form.reset({ message: '', type: data.type });
            if (handleClose) handleClose();
        }
    };

    const onCancel = () => {
        if (comment) {
            form.reset({ ...comment, type: comment.external ? 'external' : 'internal' });
        } else {
            form.reset({ message: '', type: 'internal' });
        }
        if (handleClose) {
            handleClose();
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Stack spacing={2}>
                    <ToggleButtonGroupComponent name={'type'} defaultValue={'internal'} exclusive={true}>
                        <ToggleButton value={'internal'}>{t('internal')}</ToggleButton>
                        <ToggleButton value={'external'}>{t('external')}</ToggleButton>
                    </ToggleButtonGroupComponent>

                    <ControlledInput name="message" label={t('moreInfo')} multiline={true} fullWidth={true} />

                    <Stack direction="row" spacing={1}>
                        <Button type="submit" variant="contained" disabled={watchMessage === ''}>
                            {t('save')}
                        </Button>

                        <Button variant="outlined" onClick={onCancel}>
                            {t('cancel')}
                        </Button>
                    </Stack>
                </Stack>
            </form>
        </FormProvider>
    );
};
